import type { BBox } from '@nora-monorepo/shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { NoraStateManager } from '#nora-ui/map/map/state-managers/NoraStateManager';

/**
 * Base class for state managers that fetch bbox-scoped data per visible operation.
 *
 * Owns the per-operation bbox checksums and the load/release orchestration: each visible
 * operation is fetched independently, unchanged fetch inputs skip redundant fetches, and the
 * layer refreshes once per load when anything changed. Subclasses supply the fetch call and
 * the layer refresh. Fetched data is stored per operation in dataByOperation by default;
 * subclasses with their own storage (e.g. draw layers patching FeatureCollections into
 * their DTOs) override storeOperationData/removeOperationData instead.
 */
export abstract class OperationBboxStateManager<T> extends NoraStateManager {
  /**
   * Fetched data keyed by operation id. Unused when a subclass overrides the storage hooks.
   */
  protected dataByOperation = new Map<string, T>();

  /**
   * Per-operation checksums of the last completed fetch's inputs, to avoid redundant
   * fetches on pan.
   */
  private bboxChecksums = new Map<string, string>();

  constructor(protected readonly map: NoraMap) {
    super();
  }

  /**
   * Fetches a single operation's data for the given bbox. Return undefined when there is
   * nothing to fetch or the fetch failed: nothing is stored and the checksum is not
   * updated, so the next load retries.
   */
  protected abstract fetchOperationData(bbox: BBox, operationId: string): Promise<T | undefined>;

  /**
   * Stores fetched data, returning false to reject a stale result without committing its checksum.
   */
  protected storeOperationData(operationId: string, data: T): boolean {
    this.dataByOperation.set(operationId, data);

    return true;
  }

  /**
   * Removes a released operation's stored data.
   * @returns True when data was removed (the layer needs a refresh).
   */
  protected removeOperationData(operationId: string): boolean {
    return this.dataByOperation.delete(operationId);
  }

  /**
   * Re-renders the layer that displays this manager's data.
   */
  protected abstract refreshLayer(): void | Promise<void>;

  /**
   * Checksum of every input that determines a fetch result. The default is the bbox alone;
   * override to include other inputs (e.g. which layers are visible).
   */
  protected getChecksumForBbox(bbox: BBox, _operationId: string): string {
    return bbox.getAsBBoxString();
  }

  /**
   * Loads the data of every visible operation for the current map bbox.
   */
  async loadCurrentBbox(): Promise<void> {
    const results = await Promise.allSettled(
      this.map.ref.visibleOperationIds.map(async operationId => await this.loadOperationBbox(operationId)),
    );

    // Refresh the layer once after all fetches settle, not once per operation.
    if (results.some(result => result.status === 'fulfilled' && result.value)) {
      await this.refreshLayer();
    }
  }

  /**
   * Loads a single operation's data alongside the operations already visible.
   */
  async loadOperation(operationId: string): Promise<void> {
    const newDataFetched = await this.loadOperationBbox(operationId);

    if (newDataFetched) {
      await this.refreshLayer();
    }
  }

  /**
   * Releases a single operation's data and re-renders the layer.
   */
  releaseOperation(operationId: string): void {
    this.bboxChecksums.delete(operationId);

    if (this.removeOperationData(operationId)) {
      Promise.resolve(this.refreshLayer()).catch((error) => {
        console.error('Error refreshing layer after releasing operation:', error);
      });
    }
  }

  /**
   * Clears one or all operation checksums so the next load retries unchanged fetch inputs.
   */
  protected invalidateBboxChecksums(operationId?: string): void {
    if (operationId !== undefined) {
      this.bboxChecksums.delete(operationId);
    }
    else {
      this.bboxChecksums.clear();
    }
  }

  /**
   * Clears all per-operation data and checksums. Called from the subclass unload().
   */
  protected clearOperationData(): void {
    this.dataByOperation.clear();
    this.invalidateBboxChecksums();
  }

  /**
   * Fetches and stores a single operation's data for the current bbox.
   * @returns True when new data was fetched (the layer needs a refresh).
   */
  private async loadOperationBbox(operationId: string): Promise<boolean> {
    const bbox = this.map.getMapBbox();

    // Compute the checksum before the fetch, so inputs that change while the fetch is in
    // flight (e.g. a visibility toggle) cannot be marked as already covered.
    const checksum = this.getChecksumForBbox(bbox, operationId);

    if (this.bboxChecksums.get(operationId) === checksum) {
      return false;
    }

    const data = await this.fetchOperationData(bbox, operationId);

    if (data === undefined) {
      return false;
    }

    // The operation may have been hidden or released while the fetch was in flight;
    // committing would re-add data that was just released.
    if (!this.map.ref.isOperationVisible(operationId)) {
      return false;
    }

    // The fetch inputs may have changed while the fetch was in flight (map moved, layer
    // toggled); committing would overwrite newer data with a stale response. The load for
    // the current inputs commits its own result.
    if (this.getChecksumForBbox(this.map.getMapBbox(), operationId) !== checksum) {
      return false;
    }

    if (!this.storeOperationData(operationId, data)) {
      return false;
    }

    this.bboxChecksums.set(operationId, checksum);

    return true;
  }
}
